// @flow

import React, { type Node, useState, useEffect, type Element } from 'react'
import { TextField, Typography } from '@material-ui/core'
import { core } from '../../../lib/WasmCache'
import { getErrorMsg } from '../../../lib/CoreValidations'
import { getSignUpCategoryId, getSupportedProvinceId } from '../../../lib/utils'
import { createStyles, makeStyles } from '@material-ui/core/styles'

type Props = {|
  practitionerNumber: string,
  province: string,
  category: string,
  isLfpEnrolled: boolean,
  setPractitionerNumber: (string) => void,
  setIsValid: (boolean) => void,
|}

const practitionerNumberMaxLengthMapping: { [string]: number } = {
  BC: 5,
  ON: 6,
  AB: 9,
}

const categoriesAllowedToBeBlank = [''].concat(
  core.getOptionalPractictionerNumberSignUpCategories().map((category) => category.code)
)

const usePractitionerNumberStyles = makeStyles((theme) =>
  createStyles({
    body1: {
      fontFamily: 'Roboto',
      fontSize: '14px',
      fontWeight: '300',
      textAlign: 'left',
    },
  })
)
function PractitionerNumberField({
  practitionerNumber,
  province,
  category,
  isLfpEnrolled,
  setPractitionerNumber,
  setIsValid,
}: Props): Element<typeof TextField> {
  const [errorObj, setErrorObj] = useState({ isVisible: false, msg: '' })
  const [validationResult, setValidationResult] = useState({ is_success: false, results: [] })

  useEffect(() => {
    if (province && category) {
      const defaultValue = ''
      const result = getPractitionerNumberResult(defaultValue)
      setValidationResult(result)
      setIsValid(result.is_success)
      setPractitionerNumber(defaultValue)
    }
    setErrorObj({ isVisible: false, msg: '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [province, category, isLfpEnrolled])

  const noProvinceSelected = province === '' ? 'Please select a province to bill' : ''

  const getPractitionerNumberResult = (val) => {
    return core.validateProfilePractitionerNumber(
      val,
      getSupportedProvinceId(province),
      getSignUpCategoryId(category),
      isLfpEnrolled
    )
  }

  const handleChange = (event: SyntheticInputEvent<HTMLInputElement>) => {
    const val = event.target.value
    const result = getPractitionerNumberResult(val)
    setValidationResult(result)
    setIsValid(result.is_success)
    setPractitionerNumber(val)
    setErrorObj({ isVisible: false, msg: '' })
  }

  const handleBlur = (event: SyntheticInputEvent<HTMLInputElement>) => {
    const val = event.target.value
    const result = getPractitionerNumberResult(val)
    setIsValid(result.is_success)
    setValidationResult(result)
    setErrorObj({ isVisible: true, msg: getErrorMsg(result) })
  }

  const disabled = noProvinceSelected !== '' || category === ''
  const classes = usePractitionerNumberStyles()

  const isRequired: boolean = isLfpEnrolled || !categoriesAllowedToBeBlank.includes(category)
  const labelTextOptional: string = isRequired ? '' : ' (Optional)'
  const labelText: string =
    province === 'ON'
      ? `OHIP billing number (not CPSO number)${labelTextOptional}`
      : `Practitioner number${labelTextOptional}`
  const subText: string | Node =
    province === 'BC' ? (
      <Typography variant="body1" className={classes.body1}>
        This identifies you as the practitioner performing the service. It’s not your college ID or
        any other ID. You’ll find your number
        <b>
          <a
            href="https://www.cpsbc.ca/public/registrant-directory"
            rel="noopener noreferrer"
            target="_blank"
          >
            {' '}
            here
          </a>
        </b>
        . You can also
        <b>
          <a href="https://www.dr-bill.ca/contact" rel="noopener noreferrer" target="_blank">
            {' '}
            contact us{' '}
          </a>
        </b>
        for help.
      </Typography>
    ) : (
      ''
    )

  return (
    <TextField
      label={labelText}
      id="practitioner-number"
      variant="outlined"
      value={practitionerNumber}
      onChange={handleChange}
      onBlur={handleBlur}
      inputProps={{ maxLength: practitionerNumberMaxLengthMapping[province] }}
      fullWidth
      error={!validationResult.is_success && errorObj.isVisible}
      helperText={
        !validationResult.is_success && errorObj.isVisible
          ? errorObj.msg || noProvinceSelected
          : subText
      }
      required={isRequired}
      disabled={disabled}
    />
  )
}

export default PractitionerNumberField
